/**
 * `<VerdictCard>` — Post-Interview Verdict collapsible card (B-13).
 *
 * Matches prototype `renderAnalysisCards` L685-701 (`recHtml`).
 *
 * Renders:
 *   - Card title "Post-Interview Verdict" in verdict accent color.
 *   - Inline verdict badge pill (verdict string + tinted border/bg).
 *   - `<RankDeltaBadge>` in `trailingBadge` slot when rank data is present (Q3 ruling).
 *   - Body: extracted FINAL VERDICT sentence when present, else the full
 *     recommendation markdown via `<EvaluationMarkdown>`.
 *
 * Fallback tint when no verdict extracted: teal `#00E5CC` (prototype L689, Q4 ruling).
 * Shipped had slate `#94A3B8` — fixed per prototype canon (`feedback_prototype_canon`).
 *
 * Open by default (matches prototype's expanded state for the verdict card).
 *
 * Q3 ruling (HoD): `<RankDeltaBadge>` inline in VerdictCard header next to
 * verdict pill via `trailingBadge` slot. Chip format `↑ 2 (#5→#3)`;
 * full copy as `aria-label` + `title` (condition #4).
 */
import type { JSX } from "react";
import { CollapsibleCard } from "@/components/common/CollapsibleCard";
import { EvaluationMarkdown } from "@/components/candidate-page/EvaluationMarkdown";
import { RankDeltaBadge } from "./RankDeltaBadge";
import {
  extractVerdict,
  extractFinalVerdictSentence,
  verdictColor,
  type Verdict,
} from "@/lib/interview-analysis/parsers";
import { ACCENT_TEAL } from "@/lib/tokens";

interface VerdictCardProps {
  /** Raw body of the Bottom Line / Recommendation section. */
  recommendationBody: string;
  /** Rank before (for RankDeltaBadge). Null = no badge. */
  rankBefore?: number | null;
  /** Rank after (for RankDeltaBadge). Null = no badge. */
  rankAfter?: number | null;
}

export function VerdictCard({
  recommendationBody,
  rankBefore,
  rankAfter,
}: VerdictCardProps): JSX.Element {
  const verdict: Verdict | null = extractVerdict(recommendationBody);
  // Q4 ruling: fallback tint = teal #00E5CC (prototype L689), NOT slate #94A3B8.
  const vc = verdict ? verdictColor(verdict) : ACCENT_TEAL;
  const finalVerdictSentence = extractFinalVerdictSentence(recommendationBody);

  return (
    <CollapsibleCard
      title="Post-Interview Verdict"
      defaultOpen={true}
      borderColor={vc}
      trailingBadge={
        <>
          {/* Verdict pill — inline next to title (prototype L696). */}
          {verdict ? (
            <span
              data-testid="verdict-badge"
              data-verdict={verdict}
              className="ml-2 rounded-[20px] px-2 py-0.5 text-2xs font-bold uppercase tracking-wide"
              style={{
                color: vc,
                borderWidth: "1px",
                borderStyle: "solid",
                borderColor: `${vc}44`,
                background: `${vc}18`,
                fontSize: "9px",
                padding: "2px 8px",
              }}
            >
              {verdict}
            </span>
          ) : null}
          {/* RankDeltaBadge — Q3: inline next to verdict pill via trailingBadge. */}
          <RankDeltaBadge rankBefore={rankBefore} rankAfter={rankAfter} />
        </>
      }
    >
      {finalVerdictSentence ? (
        <p
          className="text-sm leading-7 text-text-secondary"
          data-testid="verdict-final-sentence"
        >
          {finalVerdictSentence}
        </p>
      ) : (
        // Fallback: full recommendation markdown when no FINAL VERDICT extracted.
        // EvaluationMarkdown is the canonical AI-markdown renderer (no marked dep).
        <EvaluationMarkdown source={recommendationBody} />
      )}
    </CollapsibleCard>
  );
}
